import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Typography, alpha } from '@mui/material'

import { motivationColor } from '../../helpers/motivationColors'
import { formatDelta, formatSharePct } from '../../helpers/scoreDisplay'
import { deltaColor, findPattern } from '../../helpers/showcase'
import { useShowcasePatternCreatives } from '../../hooks/useShowcasePatternCreatives'
import { useShowcasePatternDetail } from '../../hooks/useShowcasePatternDetail'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUAInsightsShowcase } from '../../hooks/useUAInsightsShowcase'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { CreativeAdsTable } from '../CreativeAdsTable/CreativeAdsTable'
import { LensDropdown } from '../LensDropdown/LensDropdown'
import { ListingTruncationNote } from '../ListingTruncationNote/ListingTruncationNote'
import { SectionCard } from '../SectionCard/SectionCard'
import { ShowcaseDetailHeader } from '../ShowcaseDetailHeader/ShowcaseDetailHeader'
import { StoryPatternsTable } from '../StoryPatternsTable/StoryPatternsTable'

interface StoryPatternDetailViewProps {
  patternId: string
  /** Active motivation lens (motivation id), or null. */
  lensMotivationId: string | null
  /** When set, scope all stats/creatives to this game. */
  gameId?: string
  /** Open a pattern (defaults to dashboard navigation). */
  onOpenPattern?: (patternId: string, lensMotivationId?: string | null) => void
  /** Handle an associated-motivation click (defaults to dashboard navigation). */
  onOpenMotivation?: (motivationId: string) => void
}

/**
 * In-page Story Pattern detail view: resolves a parent pattern or sub-pattern,
 * shows its associated motivations, sub-patterns, and example creatives. When
 * arrived through a motivation, that motivation lenses the example creatives and
 * is reflected in the lens dropdown (and the header breadcrumb).
 */
export const StoryPatternDetailView: FC<StoryPatternDetailViewProps> = ({ patternId, lensMotivationId, gameId, onOpenPattern, onOpenMotivation }) => {
  const { t } = useTranslation('ua-insights')
  const { patternLabel, motivationLabel } = useUATaxonomy()
  const { motivationById } = useUAInsightsMeta()
  const nav = useUAInsightsNavigation()
  const openPattern = onOpenPattern ?? nav.openPatternView
  const openMotivation = onOpenMotivation ?? nav.openMotivationView

  // The unlensed showcase supplies the pattern hierarchy and its associated
  // motivations; the lensed one below supplies the stats.
  const { data: showcase } = useUAInsightsShowcase({ gameId })
  const { parent } = showcase ? findPattern(showcase.narrativePatterns, patternId) : { parent: null }

  // Lens-aware stats for the viewed pattern (header + sub-patterns) so the
  // numbers track the active motivation lens, not just the creatives grid.
  const patternArgs = { patternId, lensMotivationId, gameId }

  const { data: patternDetail } = useShowcasePatternDetail(patternArgs)
  const pattern = patternDetail?.pattern

  const { data: patternCreatives } = useShowcasePatternCreatives(patternArgs)
  const creatives = patternCreatives?.rows ?? []
  const creativesTotal = patternCreatives?.total
  const lensApplied = patternCreatives?.lensApplied ?? false

  if (!pattern) {
    return <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{t('showcase_pattern_not_found')}</Typography>
  }

  const desc = pattern.description || parent?.description || ''
  const narrativeMotivations = showcase?.narrativeMotivations ?? {}
  const motivations = narrativeMotivations[patternId] ?? (parent ? narrativeMotivations[parent.pattern.id] : undefined) ?? []
  const lens = lensMotivationId ? motivations.find((m) => m.id === lensMotivationId) ?? motivationById(lensMotivationId) ?? null : null

  // Note only the fallback case: a lens is active but none of the pattern's
  // creatives carry it, so the full (unlensed) list is shown instead.
  let note: string | undefined
  if (lens && !lensApplied) {
    note = t('showcase_lens_note_empty', { theme: patternLabel(pattern.pattern), motivation: motivationLabel(lens) })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ShowcaseDetailHeader
        accent="pattern"
        title={patternLabel(pattern.pattern)}
        badgeLabel={parent ? t('showcase_badge_sub_pattern') : t('showcase_badge_story_pattern')}
        desc={desc || undefined}
        titleExtra={<LensDropdown currentLens={lens} options={motivations} onSelect={(motivationId) => openPattern(patternId, motivationId)} />}
        stats={[
          { value: pattern.creativesCount, label: t('showcase_stat_creatives'), color: 'primary.main' },
          { value: formatSharePct(pattern.sharePct), label: t('showcase_stat_popularity'), color: 'primary.main' },
          { value: formatDelta(pattern.deltaPp), label: t('showcase_stat_vs_last_month'), color: deltaColor(pattern.deltaPp) },
        ]}
      />

      <SectionCard title={t('showcase_associated_motivations')}>
        {motivations.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {motivations.map((motivation) => {
              const color = motivationColor(motivation)
              const active = motivation.id === lensMotivationId
              return (
                <Box
                  key={motivation.id}
                  component="button"
                  type="button"
                  onClick={() => openMotivation(motivation.id)}
                  sx={{
                    border: '1px solid transparent',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: active ? 700 : 400,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 100,
                    bgcolor: active ? color : alpha(color, 0.1),
                    color: active ? 'common.white' : color,
                    boxShadow: active ? `0 0 0 3px ${alpha(color, 0.2)}` : 'none',
                  }}
                >
                  {motivationLabel(motivation)}
                </Box>
              )
            })}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{t('showcase_no_associated_motivations')}</Typography>
        )}
      </SectionCard>

      {pattern.children && pattern.children.length > 0 && (
        <SectionCard title={t('showcase_sub_patterns', { count: pattern.children.length })} disableBodyPadding>
          <StoryPatternsTable
            patterns={pattern.children}
            firstColumnLabel={t('showcase_col_sub_pattern')}
            onSelectPattern={(childId) => openPattern(childId, lensMotivationId)}
            hideTopGames={!!gameId}
            disableCard
          />
        </SectionCard>
      )}

      <Box>
        {note && <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>{note}</Typography>}
        <CreativeAdsTable rows={creatives} />
        {creativesTotal !== undefined && <ListingTruncationNote shown={creatives.length} total={creativesTotal} />}
      </Box>
    </Box>
  )
}
